import { Fab } from "@rmwc/fab";
import { TextField } from "@rmwc/textfield";
import { setToken, setTokenAdmin } from "App/Authentification/Store/actions";
import { selectTokenAdmin, selectTokenValue } from "App/Authentification/Store/selectors";
import { TAB_DISTRIBUTION, TAB_MAP, URL_BASE } from "App/Constants";
import { showError, showSuccess } from "App/Toast/Toast";
import { RESSOURCE_DISTRIBUTION_UTILISATEUR } from "Achat/API/AchatApi";
import { APP_LOGIN_ISIPAY as loginIsipay, APP_MDP_ISIPAY as mdpIsipay } from "Config.json";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { GET, POST } from "Request/Components/RequestUtils";
import { GET_LIST, GET_LIST_WITH_PARAMS, useJsonRequest } from "Request/Components/useJsonRequest";
import { DATA_CREATED, DATA_UPDATED } from "Request/Store/constants";
import { selectDataResourceReceived, selectDataResourceUpdated, selectErrorDataResource, selectStateDataResource } from "Request/Store/selectors";
import logoIsipay from "Resources/ISIPAY_LOGO.png";
import { RESSOURCE_AUTHENTIFICATION, RESSOURCE_AUTHENTIFICATION_USER_STORE, RESSOURCE_FORGOT_PASSWORD, RESSOURCE_FORGOT_PASSWORD_DEV, URL } from "../API/LoginAPI";
import "./Login.css";

// #region FONCTION COMPOSANT
/**
* Composant permettant de se connecter à l'application
* @class Account
* @category Login
*/
function Login() {
  // #region INITIALISATION
  // Initialisation du state
  const [canLogin, setCanLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [login, setLogin] = useState(process.env.NODE_ENV === "production" ? "" : process.env.REACT_APP_LOGIN_DEV);
  const [password, setPassword] = useState(process.env.NODE_ENV === "production" ? "" : process.env.REACT_APP_MDP_DEV);
  const [showPassword, setShowPassword] = useState(false);
  const [isCheckingDistribution, setIsCheckingDistribution] = useState(false);

  // Initialisation des selecteurs
  const dataErrorLogin = useSelector((state) => selectErrorDataResource(state, RESSOURCE_AUTHENTIFICATION_USER_STORE));
  const dataReceivedToken = useSelector((state) => selectDataResourceReceived(state, RESSOURCE_AUTHENTIFICATION));
  const dataReceivedTokenUser = useSelector((state) => selectDataResourceReceived(state, RESSOURCE_AUTHENTIFICATION_USER_STORE));
  const dataUpdatedForgetPassword = useSelector((state) => selectDataResourceUpdated(state, RESSOURCE_FORGOT_PASSWORD_DEV)); // Retour de la requête du mot de passe oublié
  const stateDataForgotPassword = useSelector((state) => selectStateDataResource(state, RESSOURCE_FORGOT_PASSWORD_DEV)); // Retour de la requête du mot de passe oublié
  const tokenAdmin = useSelector((state) => selectTokenAdmin(state));
  const tokenValue = useSelector((state) => selectTokenValue(state));
  const dataReceivedDistribution = useSelector((state) => selectDataResourceReceived(state, RESSOURCE_DISTRIBUTION_UTILISATEUR));

  // Traduction i18n
  const { t } = useTranslation();

  // Initialisation du dispatch
  const dispatch = useDispatch();

  // Récupération de l'historique
  const history = useHistory();
  const location = useLocation();
  // #endregion

  // #region UTILS
  // AYB 2026.02.26 - Normalise l'email en supprimant tous les espaces (accepte la saisie avec espaces, envoi sans espaces)
  const normalizeEmail = (email) => (email && typeof email === 'string' ? email.replace(/\s/g, '') : '');

  // AYB 2025.12.03 - Validation du format email : accepte les espaces, valide la forme normalisée (sans espaces)
  const validateEmail = (email) => {
    if (!email || typeof email !== 'string') {
      return false;
    }
    const normalized = normalizeEmail(email);
    if (!normalized) {
      return false;
    }
    const emailRegex = /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(normalized)) {
      return false;
    }
    if (normalized.length > 254) {
      return false;
    }
    if (/\.{2,}/.test(normalized) || normalized.startsWith('.') || normalized.startsWith('@') || normalized.endsWith('.')) {
      return false;
    }
    return true;
  }

  const isValidPassword = () => {
    let isValid = false;
    if (!login || !password) {
      showError(t("login.mailPasswordRequired"), { useOverlay: true, title: t("login.connection") });
      return isValid;
    }

    if (password.length < 2) {
      showError(t("login.wrongPassword"), { useOverlay: true, title: t("login.connection") });
      return isValid;
    }

    isValid = true;
    return isValid;
  };
  // #endregion

  // #region REQUEST
  // Requête de récupération du token
  const requestGetToken = useJsonRequest({
    command: POST,
    getMode: GET_LIST_WITH_PARAMS,
    resource: RESSOURCE_AUTHENTIFICATION,
    silent: true,
    tokenObligatory: false,
    tokenAdminObligatory: true,
    url: URL
  });

  // Récupération du token admin
  const requestGetTokenAdmin = useJsonRequest({
    command: POST,
    getMode: GET_LIST_WITH_PARAMS,
    resource: RESSOURCE_AUTHENTIFICATION,
    tokenObligatory: false,
    silent: true,
    url: URL
  });

  // Requête d'envoi du mail
  const requestPostMail = useJsonRequest({
    command: POST,
    resource: RESSOURCE_FORGOT_PASSWORD_DEV,
    tokenObligatory: false,
    tokenAdminObligatory: true,
    url: URL_BASE,

  });

  // AYB 2026.01.26 - Requête de récupération de la distribution pour vérifier si l'utilisateur a une distribution en cours
  const requestGetDistributionUtilisateur = useJsonRequest({
    command: GET,
    getMode: GET_LIST,
    resource: RESSOURCE_DISTRIBUTION_UTILISATEUR,
    url: URL_BASE,
  });
  // #endregion

  // #region EVENTS
  // Envoi des données de connexion au serveur
  const handleSubmitMail = () => {
    if (validateEmail(login)) {
      requestPostMail({ mail: normalizeEmail(login) });
    }
  };

  // Envoi des données de connexion au serveur
  const handleSubmitLogin = () => {
    // AYB 2025.12.03 - Validation du format email avant envoi de la requête
    if (!validateEmail(login)) {
      showError(t("login.mailIncorrect"), { useOverlay: true, title: t("login.connection") });
      return;
    }

    if (!isValidPassword()) {
      return;
    }

    let params = {
      username: normalizeEmail(login),
      password: password
    };

    requestGetToken(params, "user");
  };
  const handleBackToMap = () => {
    const mapParameters = location.state ? location.state.mapParams : "";
    history.push("/" + mapParameters, { selectedIndexTab: TAB_MAP });
  };
  // #endregion

  // #region HOOK D'EFFET
  // Récupération et gestion du token ISIPAY
  useEffect(() => {
    if (tokenAdmin && tokenAdmin.tokenAdmin) {
      return;
    }

    let params = {
      username: loginIsipay.trim(),
      password: mdpIsipay,
    };

    setCanLogin(false);
    requestGetTokenAdmin(params);
  }, [tokenAdmin, requestGetTokenAdmin]);

  // Enregistrement du token et navigation
  useEffect(() => {
    if (!dataReceivedToken) {
      return;
    }

    if (!tokenAdmin.tokenAdmin) {
      dispatch(setTokenAdmin(dataReceivedToken.token));
      setTimeout(() => {
        setCanLogin(true);
      }, 1500);
      return;
    }
  }, [dispatch, dataReceivedToken, tokenAdmin]);

  useEffect(() => {
    if (!dataReceivedTokenUser) {
      return;
    }
    dispatch(setToken(dataReceivedTokenUser.token));
  }, [dispatch, dataReceivedTokenUser]);

  // AYB 2026.01.26 - Vérification de la distribution en cours après que le token soit disponible
  useEffect(() => {
    if (!tokenValue || isCheckingDistribution) {
      return;
    }

    setIsCheckingDistribution(true);
    requestGetDistributionUtilisateur();
  }, [tokenValue, isCheckingDistribution, requestGetDistributionUtilisateur]);

  // AYB 2026.01.26 - Vérification de la distribution et redirection vers "Ma distribution" si l'utilisateur a une distribution en cours
  useEffect(() => {
    if (!isCheckingDistribution) {
      return;
    }

    if (dataReceivedDistribution === null || dataReceivedDistribution === undefined) {
      return;
    }

    const mapParameters = location.state ? location.state.mapParams : "";
    const hasDistribution = Array.isArray(dataReceivedDistribution) && dataReceivedDistribution.length > 0;

    setIsCheckingDistribution(false);

    if (hasDistribution) {
      history.push("/" + mapParameters, { selectedIndexTab: TAB_DISTRIBUTION });
    } else {
      history.push("/" + mapParameters, { selectedIndexTab: TAB_MAP });
    }
  }, [isCheckingDistribution, dataReceivedDistribution, history, location]);

  // Retour à l'écran de connexion après l'envoi de l'adresse mail
  useEffect(() => {
    if (!dataUpdatedForgetPassword) {
      return;
    }

    setIsForgotPassword(false);
  }, [dataUpdatedForgetPassword]);

  // Réception de la création du mot de passe
  useEffect(() => {
    if (stateDataForgotPassword !== DATA_CREATED) {
      return;
    }

    // Message de confirmation
    showSuccess(t("login.sendForgotPassword"));

    history.push("/connexion");
  }, [history, stateDataForgotPassword, t]);

  // Réception de la mise à jour du mot de passe
  useEffect(() => {
    if (stateDataForgotPassword !== DATA_UPDATED) {
      return;
    }

    // Message de confirmation
    showSuccess(t("login.sendForgotPassword"));

    history.push("/connexion");
  }, [history, stateDataForgotPassword, t]);

  useEffect(() => {
    if (!dataErrorLogin) {
      return;
    }
    const msg = dataErrorLogin.error;
    const text = typeof msg === "string" && msg.startsWith("TRANSLATION_KEY:")
      ? t(msg.replace("TRANSLATION_KEY:", ""))
      : t("login.mailOrPasswordIncorrect");
    showError(text, { useOverlay: true, title: t("login.connection") });
  }, [dataErrorLogin, t]);
  // #endregion

  // #region INTERFACE
  // Interface d'oubli de mot de passe
  const forgotPasswordInterface = () => (
    <>
      <div className="Login-Title">
        <h1>{t("login.forgotPassword")}</h1>
      </div>
      <TextField
        label={t("login.mail")}
        name="mail"
        type="email"
        onChange={(item) => setLogin(item.target.value)}
        outlined
        required
        value={login}
      />
      <div className="Login-Button-Container">
        <Fab
          label={t("login.validation")}
          onClick={() => handleSubmitMail()}
        />
        <div
          className="Login-Link"
          onClick={() => setIsForgotPassword(false)}
          style={{ textDecoration: "none" }}
        >
          {t("login.backToLogin")}
        </div>
      </div>
    </>
  );


  // Interface de connexion
  const loginInterface = () => (
    <>
      <div className="Login-Button-Container">
        <div className="Login-NewUser-Section">
          <p className="Login-NewUser-Text">{t("login.noAccountYet")}</p>
          <Fab
            label={t("login.createAccountLink")}
            onClick={() => history.push("/nouveauCompte")}
          />
        </div>

        <div className="Login-ExistingUser-Section">
          <p className="Login-ExistingUser-Text">{t("login.alreadyClient")}</p>
          <form
            className="Login-ExistingUser-Fields"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmitLogin();
            }}
          >
            <TextField
              label={t("login.mail")}
              name="login"
              type="email"
              onChange={(item) => setLogin(item.target.value)}
              outlined
              required
              value={login}
            />
            <TextField
              label={t("login.password")}
              name="password"
              onChange={(item) => setPassword(item.target.value)}
              outlined
              required
              type={showPassword ? "text" : "password"}
              trailingIcon={{
                icon: showPassword ? "visibility_off" : "visibility",
                size: "small",
                tabIndex: 0,
                onClick: () => setShowPassword(!showPassword)
              }}
              value={password}
            />
            <Fab
              label={t("login.connection")}
              type="submit"
              onClick={() => handleSubmitLogin()}
            />
          </form>
        </div>

        <p className="Login-Link Login-Link-NoUnderline" onClick={() => setIsForgotPassword(true)}>
          {t("login.forgotPassword?")}
        </p>

        <p 
          className="Login-Link" 
          onClick={() => handleBackToMap()}
          style={{ textDecoration: "none", margin: 0 }}
        >
           {t("login.backToMap")}
        </p>
      </div>
    </>
  );

  return (
    <div className="Login-Container" >
      <img
        alt="logo ISIPAY"
        className="Login-Logo"
        src={logoIsipay}
      />
      {isForgotPassword
        ?
        forgotPasswordInterface()
        :
        loginInterface()
      }
    </div>
  );
  // #endregion
};
// #endregion

export { Login };

